import { beginAjaxCall } from './ajaxStatusActions';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import API from '../constants/apiEndpoints';
import { msalFetch } from '../components/auth/Auth-Utils';

import * as types from './actionTypes';
import { constants } from '../constants/constants';
import { IsuggestedPart, ItableFiltersParams, ThunkResult } from '../models';
import { initialSuggestedPart } from '../reducers/initialState';
import { omit, map } from 'lodash';
const uuidv4 = require('uuid/v4');

/**
 * Backend returns SuggestedPart with partMaterial/productMaterial (Material), partMaterialID/productMaterialID.
 * Map to existing front-end shape: part/product (Material display fields), partID/productID (same IDs for form/API).
 * Ensures part/product are always objects so consumers need no defensive guards (plan rule c).
 */
function normalizeSearchSuggestedPartsResponse(payload: any): any {
    if (!payload || !Array.isArray(payload.result)) return payload;
    const result = payload.result.map((item: any) => {
        const partMaterial = item.partMaterial || item.PartMaterial;
        const productMaterial = item.productMaterial || item.ProductMaterial;
        return {
            ...item,
            partID: item.partMaterialID ?? item.partID,
            productID: item.productMaterialID ?? item.productID,
            part: partMaterial
                ? {
                      id: partMaterial.id,
                      number: partMaterial.number ?? '',
                      description:
                          partMaterial.description ?? partMaterial.name ?? '',
                      installationTime: partMaterial.installationTime ?? 0
                  }
                : { id: '', number: '', description: '', installationTime: 0 },
            product: productMaterial
                ? {
                      id: productMaterial.id,
                      name:
                          productMaterial.name ??
                          productMaterial.description ??
                          ''
                  }
                : { id: '', name: '' }
        };
    });
    return { ...payload, result };
}

export const searchSuggestedParts = (): ThunkResult<any> => {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const {
            page,
            partSearch,
            productSearch,
            productID
        } = getState().manageSuggestedParts.tableFilters;
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { page, partSearch, productSearch, productID }
        };
        const url = API.suggestedParts.search;
        return msalFetch(url, axiosOptions)
            .then(data => {
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    const normalized = normalizeSearchSuggestedPartsResponse(
                        data.data
                    );
                    dispatch({
                        type: types.SEARCH_SUGGESTED_PARTS_SUCCESS,
                        payload: normalized
                    });
                    dispatch({
                        type: types.SUGGESTED_PARTS_MANAGE_TOTAL_PAGES,
                        pages: normalized.pages ?? data.data.pages
                    });
                    return data;
                }
            })
            .catch((error: any) => {
                dispatch({
                    type: types.SEARCH_SUGGESTED_PARTS_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'searchSuggestedParts');
                console.error(error);
            });
    };
};

export function saveSuggestedPart(
    suggestedParts: IsuggestedPart[]
): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        dispatch({ type: types.CLOSE_ALL_MODALS });

        const suggestedPartsForAPI = map(suggestedParts, part => {
            const cleanedSuggestedPart = omit(part, [
                'part',
                'product',
                'partID',
                'productID',
                'installBaseID',
                'installBaseQuantity',
                'createDate',
                'updateDate'
            ]);
            return {
                ...cleanedSuggestedPart,
                id: part.id || uuidv4(),
                partMaterialID: part.partID,
                productMaterialID: part.productID
            };
        });

        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: suggestedPartsForAPI
        };

        const url = API.suggestedParts.save;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('missing data');
                }
                dispatch({
                    type: types.SAVE_SUGGESTED_PARTS_SUCCESS,
                    part: suggestedParts[0]
                });
                dispatch(searchSuggestedParts());
                return data;
            })
            .catch((error: any) => {
                dispatch({
                    type: types.SAVE_SUGGESTED_PARTS_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'get brands');
                console.error(error);
            });
    };
}

/*
 * updateSelectedSuggestedPart
 * update the selected suggestedPart
 */
export const updateSelectedSuggestedPart = (
    suggestedPart = initialSuggestedPart
) => ({
    type: types.UPDATE_SELECTED_SUGGESTED_PART,
    payload: suggestedPart
});

export const toggleEditSuggestedPartModal = (show?: boolean) => ({
    type: types.TOGGLE_MODAL_EDIT_SUGGESTED_PART,
    show
});

export const setTableFilter = (filters: ItableFiltersParams) => ({
    type: types.SET_TABLE_FILTER_MANAGE_SUGGESTED_PARTS,
    filters
});
